import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  ProactiveTemplateMapEntry,
  ProactiveTemplatePurpose,
  WhatsAppChannel,
  WhatsAppTemplate,
} from '@workspace/types';
import { whatsappAdminApi } from '../../whatsapp/api/whatsappApi';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import { ContactAgentPage, ContactAgentPageHeader } from '../components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../components/ContactAgentTenantPicker';
import { useContactAgentTenant } from '../hooks/useContactAgentTenant';

const NONE_VALUE = '__none__';

const PURPOSES: Array<{ id: ProactiveTemplatePurpose; label: string; help: string }> = [
  {
    id: 'PROMISE_KEPT_SETTLED',
    label: 'Compromiso cumplido (cuenta saldada)',
    help: 'Aviso de agradecimiento cuando el pago dejó la cuenta al día.',
  },
  {
    id: 'PROMISE_KEPT_WITH_BALANCE',
    label: 'Compromiso cumplido (con saldo)',
    help: 'Aviso cuando pagó lo prometido pero queda saldo pendiente.',
  },
  {
    id: 'PROMISE_EXPIRED',
    label: 'Compromiso vencido',
    help: 'Aviso cuando no se acreditó el pago comprometido.',
  },
  {
    id: 'WINDOW_NUDGE',
    label: 'Reactivación por silencio',
    help: 'Recordatorio para retomar la conversación cuando el deudor dejó de responder.',
  },
];

function channelLabel(channel: WhatsAppChannel): string {
  return `${channel.displayPhoneNumber} · ${channel.provider}`;
}

export function ContactAgentProactiveTemplatesPage() {
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [channels, setChannels] = useState<WhatsAppChannel[]>([]);
  const [channelId, setChannelId] = useState('');
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [mappings, setMappings] = useState<ProactiveTemplateMapEntry[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Canales CONTACTO activos: son los que usa el agente de cobranzas.
  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    setError('');
    void whatsappAdminApi
      .listChannels(tenantId)
      .then(list => {
        if (cancelled) {
          return;
        }
        const contacto = list.filter(
          channel => channel.purpose === 'CONTACTO' && channel.status === 'ACTIVE',
        );
        setChannels(contacto);
        setChannelId(current => current || contacto[0]?.id || '');
      })
      .catch(err => {
        if (!cancelled) {
          setError(contactAgentError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, tenantId]);

  // Plantillas del canal + mapeos guardados.
  useEffect(() => {
    if (!ready || !channelId) {
      setTemplates([]);
      return;
    }
    let cancelled = false;
    void Promise.all([
      whatsappAdminApi.listTemplates(channelId).catch(() => [] as WhatsAppTemplate[]),
      contactAgentApi.listProactiveTemplates().catch(() => [] as ProactiveTemplateMapEntry[]),
    ]).then(([templateList, mapList]) => {
      if (cancelled) {
        return;
      }
      setTemplates(templateList);
      setMappings(mapList);
    });
    return () => {
      cancelled = true;
    };
  }, [ready, channelId, tenantId]);

  const selectedChannel = useMemo(
    () => channels.find(channel => channel.id === channelId) ?? null,
    [channels, channelId],
  );

  // Solo plantillas aprobadas por Meta sirven fuera de la ventana de 24h.
  const approvedTemplates = useMemo(
    () => templates.filter(template => template.status === 'APPROVED'),
    [templates],
  );

  const mappingByPurpose = useMemo(() => {
    const map = new Map<ProactiveTemplatePurpose, ProactiveTemplateMapEntry>();
    for (const entry of mappings) {
      if (entry.channelId === channelId) {
        map.set(entry.purpose, entry);
      }
    }
    return map;
  }, [mappings, channelId]);

  const reloadMappings = async () => {
    const list = await contactAgentApi.listProactiveTemplates().catch(() => mappings);
    setMappings(list);
  };

  const onSelect = async (purpose: ProactiveTemplatePurpose, value: string) => {
    setBusy(true);
    setError('');
    try {
      if (value === NONE_VALUE) {
        const existing = mappingByPurpose.get(purpose);
        if (existing) {
          await contactAgentApi.deleteProactiveTemplate(existing.id);
          toast.success('Mapeo eliminado');
        }
      } else {
        await contactAgentApi.upsertProactiveTemplate({ channelId, purpose, templateId: value });
        toast.success('Mapeo guardado');
      }
      await reloadMappings();
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setBusy(false);
    }
  };

  const isWaha = selectedChannel?.provider === 'WAHA';

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title='Plantillas fuera de ventana'
        description='Elegí qué plantilla aprobada por Meta usar para cada aviso proactivo cuando la ventana de 24h de WhatsApp está cerrada. Sin mapeo, el sistema reprograma o cancela el aviso.'
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
      />

      {error ? <p className='text-destructive'>{error}</p> : null}

      <Card className='rounded-2xl shadow-sm'>
        <CardHeader className='space-y-3'>
          <CardTitle className='text-lg'>Canal CONTACTO</CardTitle>
          {channels.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No hay canales CONTACTO activos para este tenant.
            </p>
          ) : (
            <div className='max-w-md'>
              <Select value={channelId} onValueChange={setChannelId} disabled={busy}>
                <SelectTrigger aria-label='Canal'>
                  <SelectValue placeholder='Elegí un canal' />
                </SelectTrigger>
                <SelectContent>
                  {channels.map(channel => (
                    <SelectItem key={channel.id} value={channel.id}>
                      {channelLabel(channel)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardHeader>

        {channelId ? (
          <CardContent className='space-y-4'>
            {isWaha ? (
              <p className='rounded-xl border border-dashed p-3 text-sm text-muted-foreground'>
                Este canal usa WAHA, que no aplica la ventana de 24h: los avisos proactivos se
                envían como texto libre y no necesitan plantilla.
              </p>
            ) : null}

            {!isWaha && approvedTemplates.length === 0 ? (
              <p className='rounded-xl border border-dashed p-3 text-sm text-muted-foreground'>
                Este canal no tiene plantillas aprobadas por Meta. Creá y sincronizá plantillas
                UTILITY (con {'{{1}}'} = nombre, o sin variables) para poder mapearlas.
              </p>
            ) : null}

            {PURPOSES.map(purpose => {
              const current = mappingByPurpose.get(purpose.id);
              const value = current?.templateId ?? NONE_VALUE;
              const selectedTemplate = approvedTemplates.find(t => t.id === value);
              const tooManyVars = (selectedTemplate?.variableKeys.length ?? 0) > 1;
              return (
                <div
                  key={purpose.id}
                  className='flex flex-col gap-2 rounded-xl border p-3 sm:flex-row sm:items-center sm:justify-between'
                >
                  <div className='space-y-1'>
                    <p className='text-sm font-medium'>{purpose.label}</p>
                    <p className='text-xs text-muted-foreground'>{purpose.help}</p>
                    {tooManyVars ? (
                      <p className='text-xs text-destructive'>
                        La plantilla tiene más de una variable; no está soportada (usá 0 o 1).
                      </p>
                    ) : null}
                  </div>
                  <div className='flex items-center gap-2'>
                    {current ? <Badge variant='secondary'>Configurada</Badge> : null}
                    <div className='w-72'>
                      <Select
                        value={value}
                        disabled={busy || isWaha}
                        onValueChange={next => void onSelect(purpose.id, next)}
                      >
                        <SelectTrigger aria-label={purpose.label}>
                          <SelectValue placeholder='Sin plantilla' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE_VALUE}>— Sin plantilla —</SelectItem>
                          {approvedTemplates.map(template => (
                            <SelectItem key={template.id} value={template.id}>
                              {template.name} ({template.language})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        ) : null}
      </Card>
    </ContactAgentPage>
  );
}
